import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { toast } from 'sonner';
import { Coins, Check, Sparkle, Lightning, Star, Crown, ArrowSquareOut } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';

const TIER_ICON = {
  starter: Sparkle,
  grow: Lightning,
  pro: Star,
  elite: Crown,
};

const TIER_TAGLINE = {
  starter: 'For the demo drop',
  grow: 'For consistent releases',
  pro: 'The sweet spot',
  elite: 'For serious creators & teams',
};

const TIER_FEATURES = {
  starter: [
    'All platform features included',
    'AI caption generation',
    'Auto-refills each month',
    'Curator marketplace access',
    'AI image + video studio',
    'AI image batch: 1 variant',
    'Standard processing priority',
    'Basic history & analytics',
    'Standard support',
  ],
  grow: [
    'All platform features included',
    'AI caption generation',
    'Auto-refills each month',
    'Curator marketplace access',
    'AI image + video studio',
    'AI image batch: 1 variant',
    'Priority processing',
    'Extended history & analytics',
    'Priority support',
  ],
  pro: [
    'All platform features included',
    'AI caption generation',
    'Auto-refills each month',
    'Curator marketplace access',
    'AI image + video studio',
    'AI image batch: 2 variants simultaneously',
    'High processing priority',
    'Advanced history & analytics',
    'Priority support',
  ],
  elite: [
    'All platform features included',
    'AI caption generation',
    'Auto-refills each month',
    'Curator marketplace access',
    'AI image + video studio',
    'AI image batch: 4 variants simultaneously',
    'Highest processing priority',
    'Full history & analytics',
    'VIP support',
  ],
};

export default function Credits() {
  const [data, setData] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState('');
  const [creditAmount, setCreditAmount] = useState(10);
  const { user, refresh } = useAuth();

  const load = async () => {
    try {
      const [creditsRes, pricingRes] = await Promise.all([
        api.get('/credits'),
        api.get('/pricing'),
      ]);
      setData(creditsRes.data);
      setPricing(pricingRes.data);

      const max = pricingRes.data?.credit_slider?.max ?? 500;
      const min = pricingRes.data?.credit_slider?.min ?? 5;

      setCreditAmount(current => Math.min(max, Math.max(min, current)));
    } catch (e) {
      toast.error('Could not load pricing');
    }
  };

  useEffect(() => {
    load();
  }, []);

  const selectedCreditOption = pricing?.credit_options?.find(
    p => p.credits === creditAmount
  );

  const buyCredits = async () => {
    setLoading(`credits_${creditAmount}`);

    try {
      const { data: checkoutData } = await api.post('/payments/checkout', {
        credits: creditAmount,
        origin_url: window.location.origin,
      });

      window.location.href = checkoutData.checkout_url;
    } catch (e) {
      toast.error(
        e.response?.data?.detail || 'Failed to start Credits checkout'
      );
      setLoading('');
    }
  };

  const subscribe = async (lookupKey) => {
    setLoading(lookupKey);

    try {
      const { data: checkoutData } = await api.post('/payments/checkout', {
        lookup_key: lookupKey,
        quantity: 1,
        origin_url: window.location.origin,
      });

      window.location.href = checkoutData.checkout_url;
    } catch (e) {
      toast.error(
        e.response?.data?.detail || 'Failed to start subscription checkout'
      );
      setLoading('');
    }
  };

  const cancelSub = async () => {
    if (!confirm('Cancel your subscription? You will keep credits already granted.')) {
      return;
    }

    try {
      await api.post('/subscription/cancel');
      toast.success('Subscription canceled');
      await load();
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const sliderMin = pricing?.credit_slider?.min ?? 5;
  const sliderMax = pricing?.credit_slider?.max ?? 500;
  const sliderStep = pricing?.credit_slider?.step ?? 5;

  const currentPlan = pricing?.subscriptions?.find(
    p => p.tier === data?.subscription_tier
  );

  const currentSubscriptionCredits = Number(
    data?.subscription_credits ?? 0
  );

  const currentRolloverPercent = Number(
    currentPlan?.rollover_percent ?? 0
  );

  const currentRolloverEstimate = Math.round(
    Math.min(
      currentSubscriptionCredits,
      Number(currentPlan?.credits_per_month ?? 0)
    ) *
      (currentRolloverPercent / 100) *
      100
  ) / 100;

  return (
    <div className="p-6 lg:p-8 max-w-[1400px]">
      <header className="mb-8">
        <div className="font-mono text-xs text-zinc-500 uppercase tracking-wider">
          Wallet & Pricing
        </div>

        <h1 className="font-display text-4xl lg:text-5xl font-bold mt-1">
          Credits.
        </h1>

        <p className="text-zinc-400 mt-1 text-sm max-w-2xl">
          Buy Credits when you need them, or subscribe monthly for a lower
          cost per Credit.
        </p>
      </header>

      {/* Balance */}
      <div className="border border-zinc-800 rounded-xl bg-zinc-900/40 p-8 mb-8 flex items-end justify-between gap-6">
        <div>
          <div className="font-mono text-xs text-zinc-500 uppercase tracking-wider">
            Current balance
          </div>

          <div
            className="font-display text-6xl font-bold mt-2 text-[#ccff00]"
            data-testid="credit-balance"
          >
            {data?.balance == null
              ? '—'
              : Number(data.balance).toFixed(1)}
          </div>

          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2 max-w-xl">
            <div className="rounded-lg border border-zinc-800 bg-black/20 px-3 py-2">
              <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-600">
                Subscription Credits
              </div>
              <div className="mt-1 font-display text-lg text-zinc-200">
                {data?.subscription_credits == null
                  ? '0.0'
                  : Number(data.subscription_credits).toFixed(1)}
              </div>
              <div className="mt-0.5 text-[10px] font-mono text-zinc-600">
                Eligible for rollover
              </div>
            </div>

            <div className="rounded-lg border border-zinc-800 bg-black/20 px-3 py-2">
              <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-600">
                Extra Credits
              </div>
              <div className="mt-1 font-display text-lg text-zinc-200">
                {data?.purchased_credits == null
                  ? '0.0'
                  : Number(data.purchased_credits).toFixed(1)}
              </div>
              <div className="mt-0.5 text-[10px] font-mono text-zinc-600">
                Purchased / free balance
              </div>
            </div>
          </div>

          {currentPlan && currentRolloverPercent > 0 && (
            <div className="mt-3 text-xs font-mono text-zinc-500">
              <span className="text-zinc-300">
                Next renewal rollover:{' '}
                <span className="text-[#ccff00]">
                  up to {currentRolloverEstimate} Credits
                </span>
              </span>
              <span className="text-zinc-600">
                {' '}· {currentRolloverPercent}% of unused subscription Credits
              </span>
            </div>
          )}

          {data?.free_credits_active && data?.free_credits_next_at && (
            <div className="mt-3 text-xs font-mono text-zinc-500">
              <div className="text-zinc-300">
                Monthly free Credits: top up to {data.free_monthly_credits}
              </div>
              <div className="mt-1">
                Next top-up:{' '}
                {new Date(data.free_credits_next_at).toLocaleDateString(
                  undefined,
                  { year: 'numeric', month: 'long', day: 'numeric' }
                )}
              </div>
            </div>
          )}

          {!data?.free_credits_active && (
            <div className="mt-3 text-xs font-mono text-zinc-500">
              <div className="text-zinc-300">
                Monthly free Credits paused while subscribed
              </div>
            </div>
          )}

          {data?.subscription_tier && (
            <div className="mt-3 flex items-center gap-2 text-xs font-mono text-zinc-400 flex-wrap">
              <span className="px-2 py-0.5 rounded bg-[#ccff00]/15 text-[#ccff00] uppercase">
                {data.subscription_tier} plan · {data.subscription_status}
              </span>

              {data.subscription_status === 'active' && (
                <button
                  data-testid="cancel-sub-btn"
                  onClick={cancelSub}
                  className="text-zinc-500 hover:text-red-400 underline underline-offset-2"
                >
                  Cancel
                </button>
              )}
            </div>
          )}
        </div>

        <Coins
          size={64}
          weight="duotone"
          className="text-[#ccff00]/40 shrink-0"
        />
      </div>

      {/* Monthly subscriptions */}
      <section className="mb-10">
        <div className="mb-4">
          <h2 className="font-display text-2xl">Monthly subscriptions</h2>
          <p className="text-sm text-zinc-500">
            Lower cost per Credit with automatic monthly refills.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {pricing?.subscriptions?.map(p => {
            const Icon = TIER_ICON[p.tier];
            const isCurrent =
              data?.subscription_tier === p.tier &&
              data?.subscription_status === 'active';

            const highlight = p.tier === 'pro';

            return (
              <div
                key={p.tier}
                className={`relative border rounded-xl p-6 card-hover ${
                  highlight
                    ? 'border-[#ccff00] bg-[#ccff00]/5'
                    : 'border-zinc-800 bg-zinc-900/40'
                }`}
              >
                {highlight && (
                  <div className="absolute -top-2.5 left-6 px-2 py-0.5 rounded-full bg-[#ccff00] text-black text-[10px] font-mono uppercase font-bold">
                    Most popular
                  </div>
                )}

                <div className="flex items-center gap-2 mb-1">
                  <Icon
                    size={18}
                    weight="duotone"
                    className="text-[#ccff00]"
                  />
                  <span className="font-display uppercase text-sm tracking-wider">
                    {p.tier}
                  </span>
                </div>

                <div className="text-xs font-mono text-zinc-500 mb-4">
                  {TIER_TAGLINE[p.tier]}
                </div>

                <div className="font-display font-bold flex items-baseline gap-1">
                  <span className="text-5xl">
                    ${p.price_usd?.toFixed(0)}
                  </span>
                  <span className="text-sm text-zinc-500 font-mono">
                    /mo
                  </span>
                </div>

                <div className="mt-3 text-sm text-[#ccff00] font-display">
                  {p.credits_per_month} Credits / month
                </div>

                <div className="mt-1 text-xs text-zinc-600 font-mono">
                  ${p.usd_per_credit?.toFixed(3)} / Credit
                </div>

                <div className="mt-2 text-xs font-mono text-zinc-500">
                  {Number(p.rollover_percent ?? 0) > 0
                    ? `${p.rollover_percent}% rollover on unused subscription Credits`
                    : 'No monthly rollover'}
                </div>

                <ul className="mt-4 space-y-1.5 text-xs text-zinc-400 font-mono">
                  {TIER_FEATURES[p.tier]?.map(feature => (
                    <li key={feature} className="flex gap-2">
                      <Check size={12} className="text-[#ccff00] shrink-0 mt-1" />
                      {feature}
                    </li>
                  ))}
                </ul>

                <Button
                  data-testid={`subscribe-${p.tier}`}
                  disabled={isCurrent || loading === p.lookup_key}
                  onClick={() => subscribe(p.lookup_key)}
                  className={`mt-5 w-full rounded-full font-display ${
                    isCurrent
                      ? 'bg-zinc-800 text-zinc-400 cursor-not-allowed'
                      : 'btn-neon'
                  }`}
                >
                  {isCurrent
                    ? 'Current plan'
                    : loading === p.lookup_key
                      ? 'Redirecting…'
                      : 'Subscribe'}
                </Button>
              </div>
            );
          })}
        </div>
      </section>

      {/* Buy Credits */}
      <section className="mb-10">
        <div className="mb-4">
          <h2 className="font-display text-2xl">Buy Credits</h2>
          <p className="text-sm text-zinc-500">
            Move the slider in 10-Credit steps. Larger purchases automatically
            receive a lower price per Credit.
          </p>
        </div>

        <div className="border border-zinc-800 rounded-xl bg-zinc-900/40 p-6 lg:p-8">
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">
                Credits
              </div>

              <div className="font-display text-5xl lg:text-6xl font-bold mt-1">
                {creditAmount}
              </div>
            </div>

            <div className="text-right">
              <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">
                Total
              </div>

              <div className="font-display text-4xl text-[#ccff00]">
                ${selectedCreditOption?.price_usd?.toFixed(2) ?? '—'}
              </div>

              <div className="font-mono text-xs text-zinc-500 mt-1">
                ${selectedCreditOption?.usd_per_credit?.toFixed(3) ?? '—'} / Credit
              </div>
            </div>
          </div>

          <div className="mt-8">
            <input
              data-testid="credits-slider"
              type="range"
              min={sliderMin}
              max={sliderMax}
              step={sliderStep}
              value={creditAmount}
              onChange={e => setCreditAmount(Number(e.target.value))}
              className="credits-range"
            />

            <div className="flex justify-between text-[10px] font-mono text-zinc-600 mt-2">
              <span>{sliderMin}</span>
              <span>100</span>
              <span>200</span>
              <span>300</span>
              <span>400</span>
              <span>{sliderMax}</span>
            </div>
          </div>

          <div className="mt-6 flex items-center justify-between gap-4 flex-wrap">
            <div className="text-sm">
              {selectedCreditOption?.discount_pct > 0 ? (
                <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-[#ccff00]/15 text-[#ccff00] font-mono text-xs">
                  −{selectedCreditOption.discount_pct}% volume discount
                </span>
              ) : (
                <span className="text-zinc-500">
                  Base price
                </span>
              )}
            </div>

            <Button
              data-testid="buy-credits-btn"
              onClick={buyCredits}
              disabled={loading === `credits_${creditAmount}`}
              className="btn-neon rounded-full font-display px-8"
            >
              {loading === `credits_${creditAmount}`
                ? 'Redirecting…'
                : `Buy ${creditAmount} Credits`}
              <ArrowSquareOut size={14} weight="bold" className="ml-2" />
            </Button>
          </div>

          <div className="mt-5 text-[10px] font-mono text-zinc-600">
            Prices are shown in USD. Applicable tax is calculated during Stripe Checkout.
          </div>
        </div>
      </section>

      {/* Transactions */}
      <div className="border border-zinc-800 rounded-xl bg-zinc-900/40 p-6">
        <div className="font-mono text-xs text-zinc-500 uppercase tracking-wider mb-3">
          Transactions
        </div>

        {!data?.transactions?.length && (
          <div className="text-sm text-zinc-500 py-4">
            No activity yet.
          </div>
        )}

        <div className="divide-y divide-zinc-800/70">
          {data?.transactions?.map((t, i) => (
            <div
              key={i}
              className="flex items-center justify-between py-3 text-sm"
            >
              <div>
                <div className="font-medium">{t.reason}</div>
                <div className="text-xs font-mono text-zinc-500">
                  {new Date(t.created_at).toLocaleString()}
                </div>
              </div>

              <div
                className={`font-display text-lg ${
                  t.delta > 0 ? 'text-[#ccff00]' : 'text-red-400'
                }`}
              >
                {t.delta > 0 ? '+' : ''}
                {t.delta}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
